import React from "react";
import BluePenIcon from "../../../assets/icons/blue-pen-light-bg.svg";
import { useTranslation } from "react-i18next";
import { createScriptCategoryAction, deleteScriptCategoryAction, updateScriptCategoryAction } from "../../../redux/chatScript/thunks/scriptThunks";
import { message } from "antd";
import { useAppDispatch } from "../../../redux/hooks";
import EditCategoriesHelpModal from "../modals/EditCategoriesHelpModal/EditCategoriesHelpModal";
import EditCategoryActionsModal from "../modals/EditCategoryActionsModal/EditCategoryActionsModal";
import ConfirmDeleteCategoryModal from "../modals/ConfirmDeleteCategoryModal/ConfirmDeleteCategoryModal";
import EditOrCreateCategoryModal from "../modals/EditOrCreateCategoryModal/EditOrCreateCategoryModal";
import "./ScriptCategories.less";

export type ScriptCategoriesProps = {
  creatorId: string;
  categories: { _id: string; name: string }[];
  activeCategory: string;
  onSelect: (category: string) => void;
  onAfterDelete?: () => void;
};

const ScriptCategories: React.FC<ScriptCategoriesProps> = ({ creatorId, categories, activeCategory, onSelect, onAfterDelete }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [showHelp, setShowHelp] = React.useState(false);
  const [showActions, setShowActions] = React.useState(false);
  const [pressedName, setPressedName] = React.useState<string>("");
  const longPressTimer = React.useRef<number | null>(null);
  const [showConfirmDelete, setShowConfirmDelete] = React.useState(false);
  const [showEditOrCreate, setShowEditOrCreate] = React.useState(false);
  const [editMode, setEditMode] = React.useState<"create" | "edit">("create");
  const [editInitialName, setEditInitialName] = React.useState<string>("");

  const startLongPress = (name: string) => {
    if (longPressTimer.current) window.clearTimeout(longPressTimer.current);
    longPressTimer.current = window.setTimeout(() => {
      setPressedName(name);
      setShowActions(true);
    }, 2000);
  };

  const cancelLongPress = () => {
    if (longPressTimer.current) {
      window.clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  const confirmDelete = async (name: string) => {
    const toDelete = categories.find((c) => c.name === name);
    if (toDelete) {
      try {
        await dispatch(deleteScriptCategoryAction(toDelete._id)).unwrap();
        if (activeCategory === toDelete._id) {
          onSelect("all");
        }
        if (onAfterDelete) onAfterDelete();
      } catch (e: any) {
        const status = e?.status ?? e?.response?.status;
        if (status === 403) {
          message.error(t("not-authorized-to-delete-category"));
        } else {
          const backendMsg = typeof e?.message === "string" ? e.message : undefined;
          message.error(backendMsg || t("back-error-message"));
          console.error("Failed to delete category", e);
        }
      }
    }
    setShowConfirmDelete(false);
  };

  return (
    <div className="script-categories">
      <div className="script-categories-scroll" role="tablist" aria-label="script-categories" onContextMenu={(e) => e.preventDefault()}>
        <button
          type="button"
          className={`script-category-pill ${activeCategory === "all" ? "active" : ""}`}
          onClick={() => onSelect("all")}
          role="tab"
          aria-selected={activeCategory === "all"}
          aria-label="All"
          data-all="true"
        >
          <span className="pill-inner">
            <span className={`pill-icon ${activeCategory === "all" ? "active" : "inactive"}`} />
            <span className="pill-text">{t("all")}</span>
          </span>
        </button>
        {categories.map((cat, idx) => {
          const active = cat._id === activeCategory;
          return (
            <button
              key={`${cat._id}-${idx}`}
              type="button"
              className={`script-category-pill ${active ? "active" : ""}`}
              onClick={() => onSelect(cat._id)}
              onMouseDown={() => startLongPress(cat.name)}
              onMouseUp={cancelLongPress}
              onMouseLeave={cancelLongPress}
              onTouchStart={() => startLongPress(cat.name)}
              onTouchEnd={cancelLongPress}
              role="tab"
              aria-selected={active}
            >
              <span className="pill-inner">
                <span className={`pill-icon ${active ? "active" : "inactive"}`} />
                <span className="pill-text">{cat.name}</span>
              </span>
            </button>
          );
        })}
        <button type="button" className="script-categories-edit" aria-label="edit-categories" onClick={() => setShowHelp(true)}>
          <img src={BluePenIcon} alt="edit" />
        </button>
      </div>
      {showHelp && <EditCategoriesHelpModal onClose={() => setShowHelp(false)} />}
      {showActions && (
        <EditCategoryActionsModal
          name={pressedName}
          onClose={() => setShowActions(false)}
          onRequestDelete={(name) => {
            setPressedName(name);
            setShowConfirmDelete(true);
          }}
          onRequestEdit={(name) => {
            setEditMode("edit");
            setEditInitialName(name);
            setShowEditOrCreate(true);
          }}
          onRequestCreate={() => {
            setEditMode("create");
            setEditInitialName("");
            setShowEditOrCreate(true);
          }}
        />
      )}
      {showConfirmDelete && (
        <ConfirmDeleteCategoryModal
          name={pressedName}
          onCancel={() => setShowConfirmDelete(false)}
          onConfirm={async (name) => {
            confirmDelete(name);
          }}
        />
      )}
      {showEditOrCreate && (
        <EditOrCreateCategoryModal
          mode={editMode}
          initialName={editInitialName}
          onCancel={() => setShowEditOrCreate(false)}
          onConfirm={async (newName) => {
            if (editMode === "edit") {
              const toUpdate = categories.find((c) => c.name === editInitialName);
              if (toUpdate) {
                await dispatch(updateScriptCategoryAction({ id: toUpdate._id, name: newName }));
              }
            } else {
              await dispatch(createScriptCategoryAction({ name: newName, creatorId }));
            }
            setShowEditOrCreate(false);
          }}
        />
      )}
    </div>
  );
};

export default ScriptCategories;
